/**
 * @description Products workspace Discounts tab table (LYN-248).
 * @dependencies formatCurrency, PermissionGate, discounts.view|edit|delete.
 * @notes Soft-delete is status=inactive. Amount uses stored value: percent as-is, fixed_amount as cents.
 */

import React from 'react';
import { formatCurrency } from '../../utils/invoiceHelpers';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';

const formatAmount = (discount) => {
  if (discount.valueType === 'percentage') {
    return `${Number(discount.value)}%`;
  }
  if (discount.valueType === 'fixed_amount') {
    return formatCurrency(Number(discount.value) / 100);
  }
  return '—';
};

const formatMethod = (method) => (method === 'code' ? 'Code' : 'Automatic');
const formatCombination = (combination) => (combination === 'exclusive' ? 'Exclusive' : 'Stack');

const DiscountsTable = ({ discounts, onEdit, onDelete, onCreateClick }) => {
  if (discounts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <p className="text-gray-500 mb-4">No discounts found. Create your first discount to get started.</p>
        <PermissionGate permission={PERMISSIONS.discounts.create}>
          <button
            onClick={onCreateClick}
            className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-hover font-semibold transition-colors"
          >
            Create Discount
          </button>
        </PermissionGate>
      </div>
    );
  }

  const handleDelete = async (discountId) => {
    if (window.confirm('Deactivate this discount? It will no longer apply. You can edit it later to set it active again.')) {
      await onDelete(discountId);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Method
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Combination
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {discounts.map((discount) => (
              <tr key={discount.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{discount.title}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatMethod(discount.method)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {discount.code || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatCombination(discount.combination)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatAmount(discount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    discount.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                  >
                    {discount.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <PermissionGate permission={PERMISSIONS.discounts.edit}>
                    <button
                      onClick={() => onEdit(discount)}
                      className="text-brand hover:text-brand-hover mr-4 transition-colors"
                    >
                      Edit
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.discounts.delete}>
                    <button
                      onClick={() => handleDelete(discount.id)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      Deactivate
                    </button>
                  </PermissionGate>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiscountsTable;
